<template>
    <div class="brand-selection-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Search Input -->
      <div class="search-input-wrapper">
        <IconField>
          <CustomInputText
            v-model="searchQuery"
            placeholder="Найти бренд"
            class="search-input"
          />
          <InputIcon 
            v-if="!searchQuery" 
            class="pi pi-search" 
          />
          <InputIcon 
            v-else
            class="pi pi-times clear-icon" 
            @click="clearSearch"
          />
        </IconField>
      </div>

      <!-- Brand Options -->
      <div v-if="!isMobile" class="brands-grid">
        <button
          v-for="brand in filteredBrands"
          :key="brand.id"
          class="brand-option"
          :class="{ selected: isBrandSelected(brand.id) }"
          @click="toggleBrand(brand.id)"
        >
          <i class="pi pi-check" v-if="isBrandSelected(brand.id)"></i>
          <span class="brand-name">{{ brand.name }}</span>
        </button>
      </div>
      <div v-if="isMobile" class="brands-grid-mobile">
        <div class="brands-grid">
        <button
          v-for="brand in filteredBrands"
          :key="brand.id"
          class="brand-option"
          :class="{ selected: isBrandSelected(brand.id) }"
          @click="toggleBrand(brand.id)"
        >
          <i class="pi pi-check" v-if="isBrandSelected(brand.id)"></i>
          <span class="brand-name">{{ brand.name }}</span>
        </button>
      </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep } from '@/features/gift/types/gift-config'
import { useDevice } from '@/composables/useDevice'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import CustomInputText from '@/components/UI/CustomInputText/CustomInputText.vue'

interface Props {
  step: GiftStep & { type: 'brand_selection' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isMobile = computed(() => deviceType.value === 'mobile')

const searchQuery = ref('')

const selectedBrands = computed(() => {
  const data = giftStore.giftModal.giftData[props.step.id] || []
  // Миграция: если в store есть старые данные (названия), преобразуем их в ID
  if (data.length > 0 && typeof data[0] === 'string') {
    return data.map((name: string) => {
      const brand = props.step.brands.find((b) => b.name === name)
      return brand?.id
    }).filter((id: any) => id !== undefined)
  }
  return data
})

const filteredBrands = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.step.brands
  }
  const query = searchQuery.value.toLowerCase()
  return props.step.brands.filter((brand) =>
    brand.name.toLowerCase().includes(query)
  )
})

function isBrandSelected(brandId: number): boolean {
  return selectedBrands.value.includes(brandId)
}

function toggleBrand(brandId: number) {
  const currentBrands = [...selectedBrands.value]
  const index = currentBrands.indexOf(brandId)

  if (index > -1) {
    currentBrands.splice(index, 1)
  } else {
    currentBrands.push(brandId)
  }

  giftStore.updateGiftData(props.step.id, currentBrands)
}

function clearSearch() {
  searchQuery.value = ''
}

onMounted(() => {
  // Initialize with existing data if any
  if (!giftStore.giftModal.giftData[props.step.id]) {
    giftStore.updateGiftData(props.step.id, [])
  }
})
</script>

<style scoped>
.brand-selection-content {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 30px 20px 20px;
  align-self: stretch;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  text-align: left;
}

.step-title {
  font-size: 32px;
  line-height: 1.09;
  font-weight: 700;
  color: var(--primary-blue-100);
  margin: 0;
}

.step-subtitle {
  font-size: 15px;
  line-height: 1.3;
  color: var(--primary-blue-80);
  margin: 0;
}

.brands-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0;
  align-self: stretch;
}

.brand-option {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  padding: 12px;
  height: 44px;
  border: 1px solid #e6e3ec;
  border-radius: 1000px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--active-100);
}


.brand-option.selected {
  background: var(--active-100);
  border-color: var(--active-100);
  color: var(--white-100);
}

.brand-name {
  font-size: 16px;
  line-height: 1.21;
  font-weight: 400;
  color: var(--active-100);
  text-align: center;
  white-space: nowrap;
}

.brand-option.selected .brand-name {
  color: var(--white-100);
}

.search-input-wrapper {
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.search-input-wrapper :deep(.p-icon-field) {
  width: 100%;
}

.search-input-wrapper :deep(.p-inputtext) {
  width: 100%;
  padding: 12px;
  border: 1px solid #e6e3ec;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.21;
  letter-spacing: -0.04em;
  color: var(--active-100);
}

.search-input-wrapper :deep(.p-inputtext::placeholder) {
  color: rgba(35, 47, 69, 0.6);
}

.search-input-wrapper :deep(.p-icon-field-right) {
  right: 12px;
}

.search-input-wrapper :deep(.clear-icon) {
  cursor: pointer;
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .brand-selection-content {
    padding: 40px;
    gap: 40px;
    align-items: center;
  }

  .title-section {
    align-items: center;
    text-align: center;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .brands-grid {
    justify-content: center;
    gap: 10px;
    padding: 0;
    max-height: 280px;
    overflow-y: auto;
  }

  .brand-option {
    padding: 12px;
    height: 44px;
    border: 1px solid #e6e3ec;
    border-radius: 1000px;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .brand-selection-content {
    padding: var(--Padding-XXL, 40px);
    gap: 40px;
    align-items: center;
  }

  .title-section {
    align-items: center;
    text-align: center;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .brands-grid {
    justify-content: center;
    gap: 10px;
    padding: 0;
  }

  .brand-option {
    padding: 12px;
    height: 44px;
    border: 1px solid #e6e3ec;
    border-radius: 1000px;
  }
}

@media (min-width: 1920px) {
  .brand-selection-content {
    padding: 40px 120px;
    gap: 40px;
    align-items: center;
  }

  .title-section {
    align-items: center;
    text-align: center;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .brands-grid {
    justify-content: center;
    gap: 10px;
    padding: 0;
  }

  .brand-option {
    padding: 12px;
    height: 44px;
    border: 1px solid #e6e3ec;
    border-radius: 1000px;
  }
}

@media (max-width: 767px) {
  .brand-selection-content {
    padding: 30px 20px 20px;
    gap: 40px;
  }

  .title-section {
    gap: 8px;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .brands-grid-mobile {
    max-width: 100%;
    overflow-x: auto;
  }

  .brands-grid-mobile::-webkit-scrollbar {
    display: none;
  }

  .brands-grid-mobile {
    scrollbar-width: none;
  }

  .brands-grid {
    width:586px;
    gap: 10px;
    padding: 0;
  }

  .brand-option {
    padding: 12px;
    height: 44px;
    border: 1px solid #e6e3ec;
    border-radius: 1000px;
  }
}
</style>

